import React, { useState, useEffect } from 'react';
import CitySearch from './CitySearch';
import './CarRentalSearchForm.css';

/**
 * CarRentalSearchForm Component
 * 
 * Features:
 * - City autocomplete for pickup/dropoff
 * - Auto-populates from flight data
 * - Warns user if they change location/dates
 */
const CarRentalSearchForm = ({ onSearch, loading = false, flightData = null }) => {
  const [pickupCity, setPickupCity] = useState(null);
  const [dropoffCity, setDropoffCity] = useState(null);
  const [sameLocation, setSameLocation] = useState(true);
  const [pickupDate, setPickupDate] = useState('');
  const [pickupTime, setPickupTime] = useState('10:00');
  const [dropoffDate, setDropoffDate] = useState('');
  const [dropoffTime, setDropoffTime] = useState('10:00');
  
  // Track if auto-populated
  const [autoPopulated, setAutoPopulated] = useState(false);

  const today = new Date().toISOString().split('T')[0];

  // Auto-populate from flight data
  useEffect(() => {
    if (flightData && !autoPopulated) {
      // Set pickup city from flight destination
      if (flightData.destination) {
        setPickupCity({
          cityCode: flightData.destination,
          cityName: flightData.destination
        });
        
        // Same location for dropoff
        setDropoffCity({
          cityCode: flightData.destination,
          cityName: flightData.destination
        });
      }
      
      // Set pickup date/time from flight departure
      if (flightData.departureDate) {
        setPickupDate(flightData.departureDate);
      }
      
      // Set dropoff date/time from flight return
      if (flightData.returnDate) {
        setDropoffDate(flightData.returnDate);
      }
      
      setAutoPopulated(true);
    }
  }, [flightData, autoPopulated]);

  // Warn on pickup location change
  const handlePickupCityChange = (newCity) => {
    if (autoPopulated && flightData && flightData.destination) {
      if (newCity && newCity.cityCode !== flightData.destination) {
        const confirmed = window.confirm(
          `⚠️ Warning: Pickup location doesn't match flight destination!\n\n` +
          `Flight destination: ${flightData.destination}\n` +
          `Car pickup: ${newCity.cityName}\n\n` +
          `Continue with different location?`
        );
        
        if (!confirmed) {
          return;
        }
      }
    }
    setPickupCity(newCity);
    
    // Update dropoff if same location
    if (sameLocation) {
      setDropoffCity(newCity);
    }
  };

  // Warn on pickup date change
  const handlePickupDateChange = (newDate) => {
    if (autoPopulated && flightData && flightData.departureDate) {
      if (newDate !== flightData.departureDate) {
        const confirmed = window.confirm(
          `⚠️ Warning: Car pickup doesn't match flight arrival!\n\n` +
          `Flight arrival: ${flightData.departureDate}\n` +
          `Car pickup: ${newDate}\n\n` +
          `Continue with different dates?`
        );
        
        if (!confirmed) {
          return;
        }
      }
    }
    setPickupDate(newDate);
  };

  // Warn on dropoff date change
  const handleDropoffDateChange = (newDate) => {
    if (autoPopulated && flightData && flightData.returnDate) {
      if (newDate !== flightData.returnDate) {
        const confirmed = window.confirm(
          `⚠️ Warning: Car dropoff doesn't match flight departure!\n\n` +
          `Flight departure: ${flightData.returnDate}\n` +
          `Car dropoff: ${newDate}\n\n` +
          `Continue with different dates?`
        );
        
        if (!confirmed) {
          return;
        }
      }
    }
    setDropoffDate(newDate);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!pickupCity || !pickupCity.cityCode) {
      alert('Please select pickup location');
      return;
    }

    if (!pickupDate) {
      alert('Please select pickup date');
      return;
    }

    if (!dropoffDate) {
      alert('Please select dropoff date');
      return;
    }

    if (onSearch) {
      onSearch({
        pickupLocation: pickupCity.cityCode,                    // Backend expects 'pickupLocation'
        pickupDateTime: `${pickupDate}T${pickupTime}:00`,      // Backend expects 'pickupDateTime'
        returnDateTime: `${dropoffDate}T${dropoffTime}:00`,    // Backend expects 'returnDateTime'
        maxResults: 10
      });
    }
  };

  return (
    <form className="car-rental-form" onSubmit={handleSubmit}>
      {/* Auto-populate notice */}
      {autoPopulated && flightData && (
        <div className="auto-populate-notice">
          ℹ️ Auto-filled from your flight search. You can edit if needed.
        </div>
      )}

      {/* Same Location Toggle */}
      <div className="form-row">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={sameLocation}
            onChange={(e) => setSameLocation(e.target.checked)}
          />
          <span>Return to same location</span>
        </label>
      </div>

      {/* Locations Row */}
      <div className="form-row locations-row">
        <div className="form-field">
          <CitySearch
            label="Pickup Location"
            placeholder="Search city..."
            value={pickupCity}
            onChange={handlePickupCityChange}
            required
          />
        </div>

        {!sameLocation && (
          <div className="form-field">
            <CitySearch
              label="Dropoff Location"
              placeholder="Search city..."
              value={dropoffCity}
              onChange={setDropoffCity}
              required
            />
          </div>
        )}
      </div>

      {/* Pickup Date/Time Row */}
      <div className="form-row datetime-row">
        <div className="form-field">
          <label className="form-label">
            Pickup Date <span className="required">*</span>
          </label>
          <input
            type="date"
            className="form-input"
            value={pickupDate}
            onChange={(e) => handlePickupDateChange(e.target.value)}
            min={today}
            required
          />
        </div>

        <div className="form-field form-field-small">
          <label className="form-label">
            Time
          </label>
          <input
            type="time"
            className="form-input"
            value={pickupTime}
            onChange={(e) => setPickupTime(e.target.value)}
          />
        </div>
      </div>

      {/* Dropoff Date/Time Row */}
      <div className="form-row datetime-row">
        <div className="form-field">
          <label className="form-label">
            Dropoff Date <span className="required">*</span>
          </label>
          <input
            type="date"
            className="form-input"
            value={dropoffDate}
            onChange={(e) => handleDropoffDateChange(e.target.value)}
            min={pickupDate || today}
            required
          />
        </div>

        <div className="form-field form-field-small">
          <label className="form-label">
            Time
          </label>
          <input
            type="time"
            className="form-input"
            value={dropoffTime}
            onChange={(e) => setDropoffTime(e.target.value)}
          />
        </div>
      </div>

      {/* Search Button */}
      <div className="form-actions">
        <button
          type="submit"
          className="search-button"
          disabled={loading}
        >
          {loading ? (
            <>
              <div className="button-spinner"></div>
              Searching Cars...
            </>
          ) : (
            <>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                      d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
              Search Rental Cars
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default CarRentalSearchForm;